import styled from '@emotion/styled'
import { format } from 'date-fns'
import { DayPicker } from 'react-day-picker'

import { TickerUpIcon } from '@arcade/web/icons/react'

import { DatePickerProps } from './DatePicker'
import { DatePickerCaption } from './DatePickerCaption'
import { defaultCopy } from './DatePickerCopy'
import { DatePickerDialog, DialogFocusContext } from './DatePickerDialog'
import { DatePickerDropdownContextProvider } from './DatePickerDropdownContext'
import { MooncakeDatePickerClassNames } from './MooncakeDatePickerClassNames'
import { MooncakeDatePickerDropdown } from './MooncakeDatePickerDropdown'

const Container = styled.section`
  border-radius: 1rem;
  background-color: ${props => props.theme.colors.elevatedBackground};
  padding: ${props => props.theme.spacing.default};
  border: 1px solid ${props => props.theme.colors.hairline};
  user-select: none;
  grid-template-rows: max-content max-content;
  width: ${props => props.theme.pxToRem(300)};
  box-shadow: 0 12px 24px 0 #0000000a;
`

const NavIcon = () => <TickerUpIcon size={24} />

const getRangeStartFallback = (props: DatePickerProps) => {
  return [props.fromYear, props.fromMonth, props.fromDate].some(value => value != null)
    ? {}
    : { fromYear: new Date().getFullYear() - 3 }
}

const getRangeEndFallback = (props: DatePickerProps) => {
  return [props.toYear, props.toMonth, props.toDate].some(value => value != null)
    ? {}
    : { toYear: new Date().getFullYear() }
}

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

const MooncakeDatePicker = ({
  onClose,
  copy = defaultCopy,
  open = false,
  closeOnSelect,
  children,
  ...props
}: DatePickerProps) => {
  const renderPicker = () => (
    <Container>
      <DialogFocusContext.Consumer>
        {({ returnFocus }) => (
          <DatePickerDropdownContextProvider>
            {({ close: closeOpenDropdown }) => (
              <MooncakeDatePickerClassNames>
                {classNames => (
                  <DayPicker
                    {...props}
                    {...getRangeStartFallback(props)}
                    {...getRangeEndFallback(props)}
                    mode="single"
                    locale={copy.locale}
                    captionLayout="dropdown-buttons"
                    classNames={classNames}
                    components={{
                      Caption: DatePickerCaption,
                      Dropdown: MooncakeDatePickerDropdown,
                      IconRight: NavIcon,
                      IconLeft: NavIcon,
                    }}
                    formatters={{
                      formatMonthCaption: (date, options) => format(date, 'MMM', { locale: options?.locale }),
                      formatWeekdayName: (date, options) => format(date, 'EEEEE', { locale: options?.locale }),
                    }}
                    labels={{
                      labelMonthDropdown: () => copy.dropdowns.monthLabel,
                      labelYearDropdown: () => copy.dropdowns.yearLabel,
                      labelNext: () => copy.navigation.nextLabel,
                      labelPrevious: () => copy.navigation.previousLabel,
                    }}
                    onMonthChange={(...args) => {
                      closeOpenDropdown()
                      props.onMonthChange?.(...args)
                    }}
                    onSelect={async (...args) => {
                      props.onSelect?.(...args)
                      if (closeOnSelect) {
                        const transitionDelay = 80
                        await delay(transitionDelay)
                        onClose?.()
                        returnFocus()
                      }
                    }}
                  />
                )}
              </MooncakeDatePickerClassNames>
            )}
          </DatePickerDropdownContextProvider>
        )}
      </DialogFocusContext.Consumer>
    </Container>
  )

  if (!children) {
    return renderPicker()
  }

  return <DatePickerDialog open={open} onClose={onClose} renderPicker={renderPicker} renderTarget={children} />
}

export default MooncakeDatePicker
